import * as React from 'react'
import { inject, observer } from 'mobx-react'
import { message } from 'antd'
import Helmet from 'react-helmet'
import { browserHistory } from 'react-router'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import AppHeader from '../../../../components/AppView/Header'
import { AppView, AppMainView } from '../../../../components/AppView/index'
import { BlazeButton } from '../../../../components/CustomButtons/BlazeButton'
import { HelpLinkMenu } from '../../../../components/GrDropdown/HelpLinkMenu/HelpLinkMenu'
import { Routes } from '../../../../constants/Routes'
import { parseErrorMsg } from '../../../../utilities/helpers'
import { CustomTablePagination } from '../../../../components/CustomTablePagination'
import { DiscountsTable } from './DiscountsTable'
import { FilterGroup } from './FilterGroup'
import { DiscountFilter } from './DiscountFilter'
import { CurrentUserStore } from '../../../../stores/CurrentUserStore'
import { PermissionNames } from '@getgreenline/homi-shared'
import { NoPermissionsResult } from '../../../../components/NoPermissionsResult'
import { PaginationState } from './Discount.interface'
import { HelpLinks } from '../../../../constants/ExternalLinks'

interface Props {
  currentCompanyStore: CurrentCompanyStore
  currentUserStore: CurrentUserStore
}

@inject('currentCompanyStore', 'currentUserStore')
@observer
export class Discounts extends React.Component<Props, PaginationState> {
  currentCompanyStore = this.props.currentCompanyStore!
  filter = new DiscountFilter()

  constructor(props: Props) {
    super(props)

    this.state = {
      limit: 20,
      offset: 0,
    }
  }

  componentDidMount = async () => {
    const { currentCompanyStore } = this.props
    const { company } = currentCompanyStore!

    if (
      !this.props.currentUserStore.hasPermissionAtEntity(
        PermissionNames.CAN_VIEW_DISCOUNTS,
        company!.id,
      )
    )
      return

    try {
      await this.getDiscounts()
      await currentCompanyStore.getLocations()
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while loading discount lists')
    }
  }

  onClickToEditDiscount = (discountId: number) => {
    if (
      !this.props.currentUserStore.hasPermissionAtEntity(
        PermissionNames.CAN_EDIT_DISCOUNTS,
        this.currentCompanyStore!.company!.id,
      )
    ) {
      return message.error(
        `Your account does not have the "${PermissionNames.CAN_EDIT_DISCOUNTS}" permission. Please ask your administrator for access.`,
      )
    }

    window.open(
      Routes.DASHBOARD_PRODUCTS_EDIT_DISCOUNT(this.currentCompanyStore!.company!.id, discountId),
      '_blank',
    )
  }

  getDiscounts = async (offset = this.state.offset, limit = this.state.limit) => {
    this.setState({ limit, offset })
    const networkObject = this.filter.networkObject

    try {
      await this.currentCompanyStore.productStore!.getPaginatedDiscounts({
        offset,
        limit,
        ...networkObject,
      })
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while retrieving discounts')
    }
  }

  render() {
    if (
      !this.props.currentUserStore.hasPermissionAtEntity(
        PermissionNames.CAN_VIEW_DISCOUNTS,
        this.currentCompanyStore!.company!.id,
      )
    ) {
      return <NoPermissionsResult permissionName={PermissionNames.CAN_VIEW_DISCOUNTS} />
    }

    const { paginatedDiscounts } = this.currentCompanyStore.productStore!

    return (
      <AppView column>
        <Helmet>
          <title>Products | Discounts</title>
          <meta name='page-loaded' content='true' />
        </Helmet>
        <AppHeader title='Discounts'>
          <span className='spaced-button-wrapper'>
            <HelpLinkMenu
              helpLinkMenus={[
                {
                  href: HelpLinks.PRODUCTS_DISCOUNTS,
                  title: 'Adding discounts',
                },
              ]}
            />
            <BlazeButton
              buttonProps={{
                type: 'primary',
                icon: 'plus',
                onClick: () =>
                  browserHistory.push(
                    Routes.DASHBOARD_PRODUCTS_ADD_DISCOUNT(this.currentCompanyStore.company!.id),
                  ),
              }}
              titleProps={{
                title: 'Add discount',
              }}
            />
          </span>
        </AppHeader>
        <AppMainView>
          <div className='row'>
            <div className='col-md-12'>
              <FilterGroup filter={this.filter} onSearch={this.getDiscounts} />
            </div>
          </div>

          <CustomTablePagination
            showFirstPage
            paginationPosition='both'
            onClick={this.getDiscounts}
            data={paginatedDiscounts?.data || []}
            pagination={paginatedDiscounts?.pagination}
          >
            <DiscountsTable
              onClickToEditDiscount={this.onClickToEditDiscount}
              data={paginatedDiscounts?.data || []}
              loading={paginatedDiscounts === undefined}
              state={this.state}
            />
          </CustomTablePagination>
        </AppMainView>
      </AppView>
    )
  }
}
